/* ============================== TRANSLATIONS (i18n) ============================== */

import { Language } from '../types';
import enLocale from '../locales/en';

export type LocaleData = {
  statusTranslations: Record<string, string>;
  finalDecisionTranslations: Record<string, string>;
  uiText: Record<string, string>;
  badgeText: Record<string, string>;
  ui: Record<string, string>;
};

// ── Cache — EN chargé statiquement, autres langues via fetch ──────────────────
const localeCache = new Map<Language, LocaleData>([
  ['en', enLocale as LocaleData],
]);
const loading = new Map<Language, Promise<void>>();

/**
 * Amorce le cache avec une locale déjà en mémoire, sans passer par `fetch`.
 *
 * Motif : `loadLocale` récupère les langues autres que l'anglais via `fetch`, qui
 * n'existe pas en environnement de test — `t()` y renvoyait donc TOUJOURS
 * l'anglais, y compris sur un appel explicite `t('fr', …)`. Des tests censés
 * vérifier une langue validaient en réalité l'anglais, en silence.
 * Sert aussi à un préchargement synchrone (langue embarquée dans le bundle).
 */
export function registerLocale(lang: Language | string, data: LocaleData): void {
  localeCache.set(((lang as string) || 'en').toLowerCase() as Language, data);
}

export async function loadLocale(lang: Language): Promise<void> {
  const L = ((lang as string) || 'en').toLowerCase() as Language;
  if (localeCache.has(L)) return;
  if (loading.has(L)) return loading.get(L);
  const promise = fetch(`/locales/${L}.json`)
    .then(r => r.json())
    .then(data => { localeCache.set(L, data as LocaleData); })
    .catch(() => { /* fallback: en already in cache */ });
  loading.set(L, promise);
  return promise;
}

function getLocale(lang: Language | string): LocaleData {
  const L = ((lang as string) || 'en').toLowerCase() as Language;
  return localeCache.get(L) ?? enLocale;
}

// ── Rétrocompatibilité: statusTranslations etc. via Proxy ──────────────────────
export const statusTranslations: Record<Language, Record<string, string>> = new Proxy(
  {} as Record<Language, Record<string, string>>,
  { get: (_t, lang) => getLocale(lang as Language).statusTranslations }
);

export const finalDecisionTranslations: Record<Language, Record<string, string>> = new Proxy(
  {} as Record<Language, Record<string, string>>,
  { get: (_t, lang) => getLocale(lang as Language).finalDecisionTranslations }
);

export const uiText: Record<Language, Record<string, string>> = new Proxy(
  {} as Record<Language, Record<string, string>>,
  { get: (_t, lang) => getLocale(lang as Language).uiText }
);

export const badgeText: Record<Language, Record<string, string>> = new Proxy(
  {} as Record<Language, Record<string, string>>,
  { get: (_t, lang) => getLocale(lang as Language).badgeText }
);

export const ui: Record<Language, Record<string, string>> = new Proxy(
  {} as Record<Language, Record<string, string>>,
  { get: (_t, lang) => getLocale(lang as Language).ui }
);

// ── Helpers ────────────────────────────────────────────────────────────────────
export function tBadge(lang: Language, key: string): string {
  const L = (lang || "en").toLowerCase() as Language;
  return badgeText[L]?.[key] || badgeText.en[key] || key;
}

export const followUpBreakdownText: Record<Language, (a: number, p: number) => string> = {
  en: (a, p) => `Accepted: ${a} • In progress: ${p}`,
  fr: (a, p) => `Acceptés : ${a} • En cours : ${p}`,
  pt: (a, p) => `Aceitos: ${a} • Em andamento: ${p}`,
  de: (a, p) => `Akzeptiert: ${a} • Läuft: ${p}`,
  nl: (a, p) => `Geaccepteerd: ${a} • Bezig: ${p}`,
  it: (a, p) => `Accettati: ${a} • In corso: ${p}`,
  es: (a, p) => `Aceptados: ${a} • En curso: ${p}`,
  ka: (a, p) => `მიღებული: ${a} • მიმდინარე: ${p}`,
  ru: (a, p) => `Принято: ${a} • В процессе: ${p}`,
  zh: (a, p) => `已接受: ${a} • 进行中: ${p}`,
  ja: (a, p) => `受け入れ: ${a} • 進行中: ${p}`,
  he: (a, p) => `התקבל: ${a} • בתהליך: ${p}`,
  ar: (a, p) => `تم قبوله: ${a} • قيد المعالجة: ${p}`,
  sk: (a, p) => `Prijatí: ${a} • Prebieha: ${p}`,
  cs: (a, p) => `Přijatí: ${a} • Probíhá: ${p}`
};

export function tFollowUpTip(lang: Language, acceptedCount: number, inProgressCount: number): string {
  const L = (lang || "en").toLowerCase() as Language;
  const fn = followUpBreakdownText[L] || followUpBreakdownText.en;
  return fn(acceptedCount, inProgressCount);
}

// ── getSidebarConfig ───────────────────────────────────────────────────────────
export function getSidebarConfig(lang: Language) {
  const L = (lang || "en").toLowerCase() as Language;

  const dict: Partial<Record<Language, any>> = {
    en: {
      brandTitle: "TrueCalling",
      brandMeta: "Enterprise Workspace",
      items: [
        { key: "enterpriseWorkspace", label: "Dashboard", route: "/menupb2b", tags: [] },
        { key: "jobDescriptions", label: "Jobs", route: "/jdv1", tags: [] },
        { key: "fullenrich", label: "Search", route: "/aqcand", tags: [] },
        { key: "cvwa", label: "CV Library", route: "/cvwa", tags: [] },
        { type: "divider" },
        { key: "selectingCandidate", label: "Shortlist", route: "/fastscoring", tags: [] },
        { key: "submissions", label: "Assessments", route: "/submissions", tags: [] },
        { type: "divider" },
        { key: "conversations", label: "Emily Chat", route: "/conversations", tags: [] },
        { key: "pipeline", label: "Pipeline", route: "/job4manyv2", tags: [] },

        { type: "divider" },
        { type: "section", label: "Advanced Tools", items: [
          { key: "outplacement", label: "External Search", route: "/outplacement", tags: [], hideWhenCollapsed: true },
          { key: "inplacement", label: "Internal Matching", route: "/inplacement", tags: [], hideWhenCollapsed: true },
          { key: "historique", label: "Match History", route: "/historique", tags: [], hideWhenCollapsed: true },
          { key: "googlejobs", label: "Google Jobs", route: "/googlejobs", tags: [], hideWhenCollapsed: true },
          { key: "vivier", label: "Talent Pool", route: "/vivier", tags: [], hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { type: "section", label: "Admin — Bots & Prospection", adminOnly: true, items: [
          { key: "admin", label: "Admin", route: "/admin", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "writerBot", label: "Writer Bot", route: "/writer-bot", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "botCommandCenter", label: "Bot Command Center", route: "/bot-command-center", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "genialeads", label: "Genialeads", route: "/genialeads", tags: [], adminOnly: true, hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { key: "settings", label: "Settings", route: "/settings", tags: ["Enterprise", "Team"] }
      ],
      explainByKey: {
        enterpriseWorkspace: "Overview",
        jobDescriptions: "Job descriptions",
        fullenrich: "Find talent",
        cvwa: "Saved CVs",
        selectingCandidate: "Top candidates",
        submissions: "Fit results",
        conversations: "Conversations",
        pipeline: "Candidate status",
        followUp: "Track interviews and application status.",
        settings: "Manage enterprise and team settings.",
        outplacement: "Public jobs",
        inplacement: "Internal jobs",
        historique: "Saved matches",
        googlejobs: "Job search",
      }
    },
    sk: {
      brandTitle: "TrueCalling",
      brandMeta: "Pracovný priestor firmy",
      items: [
        { key: "enterpriseWorkspace", label: "Prehľad", route: "/menupb2b", tags: [] },
        { key: "jobDescriptions", label: "Pozície", route: "/jdv1", tags: [] },
        { key: "fullenrich", label: "Vyhľadávanie", route: "/aqcand", tags: [] },
        { key: "cvwa", label: "Knižnica CV", route: "/cvwa", tags: [] },
        { type: "divider" },
        { key: "selectingCandidate", label: "Užší výber", route: "/fastscoring", tags: [] },
        { key: "submissions", label: "Hodnotenia", route: "/submissions", tags: [] },
        { type: "divider" },
        { key: "conversations", label: "Chat Emily", route: "/conversations", tags: [] },
        { key: "pipeline", label: "Pipeline", route: "/job4manyv2", tags: [] },

        { type: "divider" },
        { type: "section", label: "Pokročilé nástroje", items: [
          { key: "outplacement", label: "Externé vyhľadávanie", route: "/outplacement", tags: [], hideWhenCollapsed: true },
          { key: "inplacement", label: "Interné párovanie", route: "/inplacement", tags: [], hideWhenCollapsed: true },
          { key: "historique", label: "História párovaní", route: "/historique", tags: [], hideWhenCollapsed: true },
          { key: "googlejobs", label: "Google Jobs", route: "/googlejobs", tags: [], hideWhenCollapsed: true },
          { key: "vivier", label: "Zásoba talentov", route: "/vivier", tags: [], hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { type: "section", label: "Admin — Boti a prospekcia", adminOnly: true, items: [
          { key: "admin", label: "Admin", route: "/admin", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "writerBot", label: "Writer Bot", route: "/writer-bot", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "botCommandCenter", label: "Centrum riadenia botov", route: "/bot-command-center", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "genialeads", label: "Genialeads", route: "/genialeads", tags: [], adminOnly: true, hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { key: "settings", label: "Nastavenia", route: "/settings", tags: ["Firma", "Tím"] }
      ],
      explainByKey: {
        enterpriseWorkspace: "Prehľad",
        jobDescriptions: "Opisy pozícií",
        fullenrich: "Nájsť talenty",
        cvwa: "Uložené CV",
        selectingCandidate: "Najlepší kandidáti",
        submissions: "Výsledky zhody",
        conversations: "Konverzácie",
        pipeline: "Stav kandidátov",
        followUp: "Sledujte pohovory a stav prihlášok.",
        settings: "Spravujte nastavenia firmy a tímu.",
        outplacement: "Verejné pozície",
        inplacement: "Interné pozície",
        historique: "Uložené párovania",
        googlejobs: "Vyhľadávanie pozícií",
      }
    },
    cs: {
      brandTitle: "TrueCalling",
      brandMeta: "Pracovní prostor firmy",
      items: [
        { key: "enterpriseWorkspace", label: "Přehled", route: "/menupb2b", tags: [] },
        { key: "jobDescriptions", label: "Pozice", route: "/jdv1", tags: [] },
        { key: "fullenrich", label: "Vyhledávání", route: "/aqcand", tags: [] },
        { key: "cvwa", label: "Knihovna CV", route: "/cvwa", tags: [] },
        { type: "divider" },
        { key: "selectingCandidate", label: "Užší výběr", route: "/fastscoring", tags: [] },
        { key: "submissions", label: "Hodnocení", route: "/submissions", tags: [] },
        { type: "divider" },
        { key: "conversations", label: "Chat Emily", route: "/conversations", tags: [] },
        { key: "pipeline", label: "Pipeline", route: "/job4manyv2", tags: [] },

        { type: "divider" },
        { type: "section", label: "Pokročilé nástroje", items: [
          { key: "outplacement", label: "Externí vyhledávání", route: "/outplacement", tags: [], hideWhenCollapsed: true },
          { key: "inplacement", label: "Interní párování", route: "/inplacement", tags: [], hideWhenCollapsed: true },
          { key: "historique", label: "Historie párování", route: "/historique", tags: [], hideWhenCollapsed: true },
          { key: "googlejobs", label: "Google Jobs", route: "/googlejobs", tags: [], hideWhenCollapsed: true },
          { key: "vivier", label: "Zásoba talentů", route: "/vivier", tags: [], hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { type: "section", label: "Admin — Boti a prospekce", adminOnly: true, items: [
          { key: "admin", label: "Admin", route: "/admin", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "writerBot", label: "Writer Bot", route: "/writer-bot", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "botCommandCenter", label: "Centrum řízení botů", route: "/bot-command-center", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "genialeads", label: "Genialeads", route: "/genialeads", tags: [], adminOnly: true, hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { key: "settings", label: "Nastavení", route: "/settings", tags: ["Firma", "Tým"] }
      ],
      explainByKey: {
        enterpriseWorkspace: "Přehled",
        jobDescriptions: "Popisy pozic",
        fullenrich: "Najít talenty",
        cvwa: "Uložená CV",
        selectingCandidate: "Nejlepší kandidáti",
        submissions: "Výsledky shody",
        conversations: "Konverzace",
        pipeline: "Stav kandidátů",
        followUp: "Sledujte pohovory a stav přihlášek.",
        settings: "Spravujte nastavení firmy a týmu.",
        outplacement: "Veřejné pozice",
        inplacement: "Interní pozice",
        historique: "Uložená párování",
        googlejobs: "Vyhledávání pozic",
      }
    },
    fr: {
      brandTitle: "TrueCalling",
      brandMeta: "Espace Entreprise",
      items: [
        { key: "enterpriseWorkspace", label: "Tableau de bord", route: "/menupb2b", tags: [] },
        { key: "jobDescriptions", label: "Postes", route: "/jdv1", tags: [] },
        { key: "fullenrich", label: "Recherche", route: "/aqcand", tags: [] },
        { key: "cvwa", label: "Bibliothèque CV", route: "/cvwa", tags: [] },
        { type: "divider" },
        { key: "selectingCandidate", label: "Présélection", route: "/fastscoring", tags: [] },
        { key: "submissions", label: "Évaluations", route: "/submissions", tags: [] },
        { type: "divider" },
        { key: "conversations", label: "Chat Emily", route: "/conversations", tags: [] },
        { key: "pipeline", label: "Pipeline", route: "/job4manyv2", tags: [] },

        { type: "divider" },
        { type: "section", label: "Outils avancés", items: [
          { key: "outplacement", label: "Recherche externe", route: "/outplacement", tags: [], hideWhenCollapsed: true },
          { key: "inplacement", label: "Matching interne", route: "/inplacement", tags: [], hideWhenCollapsed: true },
          { key: "historique", label: "Historique", route: "/historique", tags: [], hideWhenCollapsed: true },
          { key: "googlejobs", label: "Google Jobs", route: "/googlejobs", tags: [], hideWhenCollapsed: true },
          { key: "vivier", label: "Vivier", route: "/vivier", tags: [], hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { type: "section", label: "Admin — Bots & Prospection", adminOnly: true, items: [
          { key: "admin", label: "Admin", route: "/admin", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "writerBot", label: "Writer Bot", route: "/writer-bot", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "botCommandCenter", label: "Bot Command Center", route: "/bot-command-center", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "genialeads", label: "Genialeads", route: "/genialeads", tags: [], adminOnly: true, hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { key: "settings", label: "Paramètres", route: "/settings", tags: ["Entreprise", "Équipe"] }
      ],
      explainByKey: {
        enterpriseWorkspace: "Vue globale",
        jobDescriptions: "Fiches de poste",
        fullenrich: "Trouver des talents",
        cvwa: "CV sauvegardés",
        selectingCandidate: "Meilleurs candidats",
        submissions: "Résultats d'adéquation",
        conversations: "Conversations",
        pipeline: "Statut candidats",
        followUp: "Suivre les entretiens et le statut des candidatures.",
        settings: "Gérer les paramètres de l'entreprise et de l'équipe.",
        outplacement: "Offres publiques",
        inplacement: "Postes internes",
        historique: "Matchs sauvegardés",
        googlejobs: "Recherche d'emploi",
      }
    },
    pt: {
      brandTitle: "TrueCalling",
      brandMeta: "Espaço Empresarial",
      items: [
        { key: "enterpriseWorkspace", label: "Painel", route: "/menupb2b", tags: [] },
        { key: "jobDescriptions", label: "Vagas", route: "/jdv1", tags: [] },
        { key: "fullenrich", label: "Pesquisa", route: "/aqcand", tags: [] },
        { key: "cvwa", label: "Biblioteca de CVs", route: "/cvwa", tags: [] },
        { type: "divider" },
        { key: "selectingCandidate", label: "Pré-seleção", route: "/fastscoring", tags: [] },
        { key: "submissions", label: "Avaliações", route: "/submissions", tags: [] },
        { type: "divider" },
        { key: "conversations", label: "Chat Emily", route: "/conversations", tags: [] },
        { key: "pipeline", label: "Pipeline", route: "/job4manyv2", tags: [] },

        { type: "divider" },
        { type: "section", label: "Ferramentas avançadas", items: [
          { key: "outplacement", label: "Pesquisa externa", route: "/outplacement", tags: [], hideWhenCollapsed: true },
          { key: "inplacement", label: "Matching interno", route: "/inplacement", tags: [], hideWhenCollapsed: true },
          { key: "historique", label: "Histórico", route: "/historique", tags: [], hideWhenCollapsed: true },
          { key: "googlejobs", label: "Google Jobs", route: "/googlejobs", tags: [], hideWhenCollapsed: true },
          { key: "vivier", label: "Reserva de talentos", route: "/vivier", tags: [], hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { type: "section", label: "Admin — Bots & Prospection", adminOnly: true, items: [
          { key: "admin", label: "Admin", route: "/admin", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "writerBot", label: "Writer Bot", route: "/writer-bot", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "botCommandCenter", label: "Bot Command Center", route: "/bot-command-center", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "genialeads", label: "Genialeads", route: "/genialeads", tags: [], adminOnly: true, hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { key: "settings", label: "Configurações", route: "/settings", tags: ["Empresa", "Equipe"] }
      ],
      explainByKey: {
        enterpriseWorkspace: "Visão geral",
        jobDescriptions: "Descrições de vaga",
        fullenrich: "Encontrar talentos",
        cvwa: "CVs salvos",
        selectingCandidate: "Melhores candidatos",
        submissions: "Resultados de compatibilidade",
        conversations: "Conversas",
        pipeline: "Status dos candidatos",
        followUp: "Acompanhar entrevistas e status das candidaturas.",
        settings: "Gerenciar configurações da empresa e da equipe.",
        outplacement: "Vagas públicas",
        inplacement: "Vagas internas",
        historique: "Matches salvos",
        googlejobs: "Pesquisa de vagas",
      }
    },
    de: {
      brandTitle: "TrueCalling",
      brandMeta: "Unternehmensbereich",
      items: [
        { key: "enterpriseWorkspace", label: "Dashboard", route: "/menupb2b", tags: [] },
        { key: "jobDescriptions", label: "Jobs", route: "/jdv1", tags: [] },
        { key: "fullenrich", label: "Suche", route: "/aqcand", tags: [] },
        { key: "cvwa", label: "CV-Bibliothek", route: "/cvwa", tags: [] },
        { type: "divider" },
        { key: "selectingCandidate", label: "Auswahl", route: "/fastscoring", tags: [] },
        { key: "submissions", label: "Bewertungen", route: "/submissions", tags: [] },
        { type: "divider" },
        { key: "conversations", label: "Emily Chat", route: "/conversations", tags: [] },
        { key: "pipeline", label: "Pipeline", route: "/job4manyv2", tags: [] },

        { type: "divider" },
        { type: "section", label: "Erweiterte Tools", items: [
          { key: "outplacement", label: "Externe Suche", route: "/outplacement", tags: [], hideWhenCollapsed: true },
          { key: "inplacement", label: "Internes Matching", route: "/inplacement", tags: [], hideWhenCollapsed: true },
          { key: "historique", label: "Verlauf", route: "/historique", tags: [], hideWhenCollapsed: true },
          { key: "googlejobs", label: "Google Jobs", route: "/googlejobs", tags: [], hideWhenCollapsed: true },
          { key: "vivier", label: "Talentpool", route: "/vivier", tags: [], hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { type: "section", label: "Admin — Bots & Prospection", adminOnly: true, items: [
          { key: "admin", label: "Admin", route: "/admin", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "writerBot", label: "Writer Bot", route: "/writer-bot", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "botCommandCenter", label: "Bot Command Center", route: "/bot-command-center", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "genialeads", label: "Genialeads", route: "/genialeads", tags: [], adminOnly: true, hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { key: "settings", label: "Einstellungen", route: "/settings", tags: ["Unternehmen", "Team"] }
      ],
      explainByKey: {
        enterpriseWorkspace: "Übersicht",
        jobDescriptions: "Stellenbeschreibungen",
        fullenrich: "Talente finden",
        cvwa: "Gespeicherte CVs",
        selectingCandidate: "Top-Kandidaten",
        submissions: "Fit-Ergebnisse",
        conversations: "Gespräche",
        pipeline: "Kandidatenstatus",
        followUp: "Interviews und Bewerbungsstatus nachverfolgen.",
        settings: "Unternehmens- und Teameinstellungen verwalten.",
        outplacement: "Öffentliche Jobs",
        inplacement: "Interne Jobs",
        historique: "Gespeicherte Matches",
        googlejobs: "Jobsuche",
      }
    },
    nl: {
      brandTitle: "TrueCalling",
      brandMeta: "Bedrijfsomgeving",
      items: [
        { key: "enterpriseWorkspace", label: "Dashboard", route: "/menupb2b", tags: [] },
        { key: "jobDescriptions", label: "Vacatures", route: "/jdv1", tags: [] },
        { key: "fullenrich", label: "Zoeken", route: "/aqcand", tags: [] },
        { key: "cvwa", label: "Cv-bibliotheek", route: "/cvwa", tags: [] },
        { type: "divider" },
        { key: "selectingCandidate", label: "Selectie", route: "/fastscoring", tags: [] },
        { key: "submissions", label: "Beoordelingen", route: "/submissions", tags: [] },
        { type: "divider" },
        { key: "conversations", label: "Emily Chat", route: "/conversations", tags: [] },
        { key: "pipeline", label: "Pipeline", route: "/job4manyv2", tags: [] },

        { type: "divider" },
        { type: "section", label: "Geavanceerde tools", items: [
          { key: "outplacement", label: "Extern zoeken", route: "/outplacement", tags: [], hideWhenCollapsed: true },
          { key: "inplacement", label: "Interne matching", route: "/inplacement", tags: [], hideWhenCollapsed: true },
          { key: "historique", label: "Geschiedenis", route: "/historique", tags: [], hideWhenCollapsed: true },
          { key: "googlejobs", label: "Google Jobs", route: "/googlejobs", tags: [], hideWhenCollapsed: true },
          { key: "vivier", label: "Talentenpool", route: "/vivier", tags: [], hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { type: "section", label: "Admin — Bots & Prospectie", adminOnly: true, items: [
          { key: "admin", label: "Admin", route: "/admin", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "writerBot", label: "Writer Bot", route: "/writer-bot", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "botCommandCenter", label: "Bot Command Center", route: "/bot-command-center", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "genialeads", label: "Genialeads", route: "/genialeads", tags: [], adminOnly: true, hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { key: "settings", label: "Instellingen", route: "/settings", tags: ["Bedrijf", "Team"] }
      ],
      explainByKey: {
        enterpriseWorkspace: "Overzicht",
        jobDescriptions: "Functiebeschrijvingen",
        fullenrich: "Talent vinden",
        cvwa: "Opgeslagen cv's",
        selectingCandidate: "Topkandidaten",
        submissions: "Fit-resultaten",
        conversations: "Gesprekken",
        pipeline: "Kandidaatstatus",
        followUp: "Volg gesprekken en de status van sollicitaties op.",
        settings: "Beheer bedrijfs- en teaminstellingen.",
        outplacement: "Openbare vacatures",
        inplacement: "Interne vacatures",
        historique: "Opgeslagen matches",
        googlejobs: "Vacatures zoeken",
      }
    },
    it: {
      brandTitle: "TrueCalling",
      brandMeta: "Area Aziendale",
      items: [
        { key: "enterpriseWorkspace", label: "Dashboard", route: "/menupb2b", tags: [] },
        { key: "jobDescriptions", label: "Posizioni", route: "/jdv1", tags: [] },
        { key: "fullenrich", label: "Ricerca", route: "/aqcand", tags: [] },
        { key: "cvwa", label: "Libreria CV", route: "/cvwa", tags: [] },
        { type: "divider" },
        { key: "selectingCandidate", label: "Shortlist", route: "/fastscoring", tags: [] },
        { key: "submissions", label: "Valutazioni", route: "/submissions", tags: [] },
        { type: "divider" },
        { key: "conversations", label: "Chat Emily", route: "/conversations", tags: [] },
        { key: "pipeline", label: "Pipeline", route: "/job4manyv2", tags: [] },

        { type: "divider" },
        { type: "section", label: "Strumenti avanzati", items: [
          { key: "outplacement", label: "Ricerca esterna", route: "/outplacement", tags: [], hideWhenCollapsed: true },
          { key: "inplacement", label: "Matching interno", route: "/inplacement", tags: [], hideWhenCollapsed: true },
          { key: "historique", label: "Cronologia", route: "/historique", tags: [], hideWhenCollapsed: true },
          { key: "googlejobs", label: "Google Jobs", route: "/googlejobs", tags: [], hideWhenCollapsed: true },
          { key: "vivier", label: "Bacino di talenti", route: "/vivier", tags: [], hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { type: "section", label: "Admin — Bots & Prospection", adminOnly: true, items: [
          { key: "admin", label: "Admin", route: "/admin", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "writerBot", label: "Writer Bot", route: "/writer-bot", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "botCommandCenter", label: "Bot Command Center", route: "/bot-command-center", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "genialeads", label: "Genialeads", route: "/genialeads", tags: [], adminOnly: true, hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { key: "settings", label: "Impostazioni", route: "/settings", tags: ["Azienda", "Team"] }
      ],
      explainByKey: {
        enterpriseWorkspace: "Panoramica",
        jobDescriptions: "Descrizioni ruolo",
        fullenrich: "Trova talenti",
        cvwa: "CV salvati",
        selectingCandidate: "Migliori candidati",
        submissions: "Risultati di fit",
        conversations: "Conversazioni",
        pipeline: "Stato candidati",
        followUp: "Traccia colloqui e stato delle candidature.",
        settings: "Gestisci impostazioni azienda e team.",
        outplacement: "Offerte pubbliche",
        inplacement: "Posizioni interne",
        historique: "Match salvati",
        googlejobs: "Ricerca lavoro",
      }
    },
    es: {
      brandTitle: "TrueCalling",
      brandMeta: "Espacio Empresa",
      items: [
        { key: "enterpriseWorkspace", label: "Panel", route: "/menupb2b", tags: [] },
        { key: "jobDescriptions", label: "Puestos", route: "/jdv1", tags: [] },
        { key: "fullenrich", label: "Búsqueda", route: "/aqcand", tags: [] },
        { key: "cvwa", label: "Biblioteca de CVs", route: "/cvwa", tags: [] },
        { type: "divider" },
        { key: "selectingCandidate", label: "Preselección", route: "/fastscoring", tags: [] },
        { key: "submissions", label: "Evaluaciones", route: "/submissions", tags: [] },
        { type: "divider" },
        { key: "conversations", label: "Chat Emily", route: "/conversations", tags: [] },
        { key: "pipeline", label: "Pipeline", route: "/job4manyv2", tags: [] },

        { type: "divider" },
        { type: "section", label: "Herramientas avanzadas", items: [
          { key: "outplacement", label: "Búsqueda externa", route: "/outplacement", tags: [], hideWhenCollapsed: true },
          { key: "inplacement", label: "Matching interno", route: "/inplacement", tags: [], hideWhenCollapsed: true },
          { key: "historique", label: "Historial", route: "/historique", tags: [], hideWhenCollapsed: true },
          { key: "googlejobs", label: "Google Jobs", route: "/googlejobs", tags: [], hideWhenCollapsed: true },
          { key: "vivier", label: "Cantera de talento", route: "/vivier", tags: [], hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { type: "section", label: "Admin — Bots & Prospection", adminOnly: true, items: [
          { key: "admin", label: "Admin", route: "/admin", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "writerBot", label: "Writer Bot", route: "/writer-bot", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "botCommandCenter", label: "Bot Command Center", route: "/bot-command-center", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "genialeads", label: "Genialeads", route: "/genialeads", tags: [], adminOnly: true, hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { key: "settings", label: "Configuración", route: "/settings", tags: ["Empresa", "Equipo"] }
      ],
      explainByKey: {
        enterpriseWorkspace: "Vista general",
        jobDescriptions: "Descripciones de puesto",
        fullenrich: "Encontrar talento",
        cvwa: "CVs guardados",
        selectingCandidate: "Mejores candidatos",
        submissions: "Resultados de compatibilidad",
        conversations: "Conversaciones",
        pipeline: "Estado de candidatos",
        followUp: "Hacer seguimiento de entrevistas y estado de candidaturas.",
        settings: "Gestionar configuración de empresa y equipo.",
        outplacement: "Ofertas públicas",
        inplacement: "Puestos internos",
        historique: "Matches guardados",
        googlejobs: "Búsqueda de empleo",
      }
    },
    ka: {
      brandTitle: "TrueCalling",
      brandMeta: "კომპანიის სივრცე",
      items: [
        { key: "enterpriseWorkspace", label: "დაფა", route: "/menupb2b", tags: [] },
        { key: "jobDescriptions", label: "ვაკანსიები", route: "/jdv1", tags: [] },
        { key: "fullenrich", label: "ძიება", route: "/aqcand", tags: [] },
        { key: "cvwa", label: "CV ბიბლიოთეკა", route: "/cvwa", tags: [] },
        { type: "divider" },
        { key: "selectingCandidate", label: "შერჩევა", route: "/fastscoring", tags: [] },
        { key: "submissions", label: "შეფასებები", route: "/submissions", tags: [] },
        { type: "divider" },
        { key: "conversations", label: "Emily Chat", route: "/conversations", tags: [] },
        { key: "pipeline", label: "Pipeline", route: "/job4manyv2", tags: [] },

        { type: "divider" },
        { type: "section", label: "გაფართოებული ხელსაწყოები", items: [
          { key: "outplacement", label: "გარე ძიება", route: "/outplacement", tags: [], hideWhenCollapsed: true },
          { key: "inplacement", label: "შიდა Matching", route: "/inplacement", tags: [], hideWhenCollapsed: true },
          { key: "historique", label: "ისტორია", route: "/historique", tags: [], hideWhenCollapsed: true },
          { key: "googlejobs", label: "Google Jobs", route: "/googlejobs", tags: [], hideWhenCollapsed: true },
          { key: "vivier", label: "ტალანტების ბაზა", route: "/vivier", tags: [], hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { type: "section", label: "Admin — ბოტები", adminOnly: true, items: [
          { key: "writerBot", label: "Writer Bot", route: "/writer-bot", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "botCommandCenter", label: "ბოტის ბრძანებების ცენტრი", route: "/bot-command-center", tags: [], adminOnly: true, hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { key: "settings", label: "პარამეტრები", route: "/settings", tags: ["საწარმო", "გუნდი"] }
      ],
      explainByKey: {
        enterpriseWorkspace: "მიმოხილვა",
        jobDescriptions: "სამუშაო აღწერები",
        fullenrich: "ტალანტების პოვნა",
        cvwa: "შენახული CV-ები",
        selectingCandidate: "საუკეთესო კანდიდატები",
        submissions: "შესაბამისობის შედეგები",
        conversations: "საუბრები",
        pipeline: "კანდიდატის სტატუსი",
        followUp: "ინტერვიუებისა და განაცხადების სტატუსის ყოლოდილება.",
        settings: "საწარმოს და გუნდის პარამეტრების მართვა.",
        outplacement: "საჯარო ვაკანსიები",
        inplacement: "შიდა ვაკანსიები",
        historique: "შენახული შედეგები",
        googlejobs: "სამუშაოს ძიება",
      }
    },
    ru: {
      brandTitle: "TrueCalling",
      brandMeta: "Рабочая область компании",
      items: [
        { key: "enterpriseWorkspace", label: "Панель", route: "/menupb2b", tags: [] },
        { key: "jobDescriptions", label: "Вакансии", route: "/jdv1", tags: [] },
        { key: "fullenrich", label: "Поиск", route: "/aqcand", tags: [] },
        { key: "cvwa", label: "Библиотека CV", route: "/cvwa", tags: [] },
        { type: "divider" },
        { key: "selectingCandidate", label: "Шортлист", route: "/fastscoring", tags: [] },
        { key: "submissions", label: "Оценки", route: "/submissions", tags: [] },
        { type: "divider" },
        { key: "conversations", label: "Чат Emily", route: "/conversations", tags: [] },
        { key: "pipeline", label: "Pipeline", route: "/job4manyv2", tags: [] },

        { type: "divider" },
        { type: "section", label: "Расширенные инструменты", items: [
          { key: "outplacement", label: "Внешний поиск", route: "/outplacement", tags: [], hideWhenCollapsed: true },
          { key: "inplacement", label: "Внутренний матчинг", route: "/inplacement", tags: [], hideWhenCollapsed: true },
          { key: "historique", label: "История", route: "/historique", tags: [], hideWhenCollapsed: true },
          { key: "googlejobs", label: "Google Jobs", route: "/googlejobs", tags: [], hideWhenCollapsed: true },
          { key: "vivier", label: "Кадровый резерв", route: "/vivier", tags: [], hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { type: "section", label: "Администратор — Боты", adminOnly: true, items: [
          { key: "writerBot", label: "Writer Bot", route: "/writer-bot", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "botCommandCenter", label: "Центр управления ботами", route: "/bot-command-center", tags: [], adminOnly: true, hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { key: "settings", label: "Настройки", route: "/settings", tags: ["Предприятие", "Команда"] }
      ],
      explainByKey: {
        enterpriseWorkspace: "Обзор",
        jobDescriptions: "Описания вакансий",
        fullenrich: "Найти таланты",
        cvwa: "Сохраненные CV",
        selectingCandidate: "Лучшие кандидаты",
        submissions: "Результаты соответствия",
        conversations: "Диалоги",
        pipeline: "Статус кандидатов",
        followUp: "Отслеживание статуса интервью и заявок.",
        settings: "Управление параметрами предприятия и команды.",
        outplacement: "Открытые вакансии",
        inplacement: "Внутренние вакансии",
        historique: "Сохраненные совпадения",
        googlejobs: "Поиск работы",
      }
    },
    zh: {
      brandTitle: "TrueCalling",
      brandMeta: "企业工作区",
      items: [
        { key: "enterpriseWorkspace", label: "仪表板", route: "/menupb2b", tags: [] },
        { key: "jobDescriptions", label: "职位", route: "/jdv1", tags: [] },
        { key: "fullenrich", label: "搜索", route: "/aqcand", tags: [] },
        { key: "cvwa", label: "简历库", route: "/cvwa", tags: [] },
        { type: "divider" },
        { key: "selectingCandidate", label: "候选名单", route: "/fastscoring", tags: [] },
        { key: "submissions", label: "评估", route: "/submissions", tags: [] },
        { type: "divider" },
        { key: "conversations", label: "Emily 聊天", route: "/conversations", tags: [] },
        { key: "pipeline", label: "Pipeline", route: "/job4manyv2", tags: [] },

        { type: "divider" },
        { type: "section", label: "高级工具", items: [
          { key: "outplacement", label: "外部搜索", route: "/outplacement", tags: [], hideWhenCollapsed: true },
          { key: "inplacement", label: "内部匹配", route: "/inplacement", tags: [], hideWhenCollapsed: true },
          { key: "historique", label: "匹配历史", route: "/historique", tags: [], hideWhenCollapsed: true },
          { key: "googlejobs", label: "Google Jobs", route: "/googlejobs", tags: [], hideWhenCollapsed: true },
          { key: "vivier", label: "人才库", route: "/vivier", tags: [], hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { type: "section", label: "管理员-机器人", adminOnly: true, items: [
          { key: "writerBot", label: "Writer Bot", route: "/writer-bot", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "botCommandCenter", label: "机器人命令中心", route: "/bot-command-center", tags: [], adminOnly: true, hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { key: "settings", label: "设置", route: "/settings", tags: ["企业", "团队"] }
      ],
      explainByKey: {
        enterpriseWorkspace: "总览",
        jobDescriptions: "职位描述",
        fullenrich: "寻找人才",
        cvwa: "已保存简历",
        selectingCandidate: "优选候选人",
        submissions: "匹配结果",
        conversations: "对话",
        pipeline: "候选人状态",
        followUp: "跟踪面试和申请状态。",
        settings: "管理企业和团队设置。",
        outplacement: "公开职位",
        inplacement: "内部职位",
        historique: "已保存匹配",
        googlejobs: "职位搜索",
      }
    },
    ja: {
      brandTitle: "TrueCalling",
      brandMeta: "企業ワークスペース",
      items: [
        { key: "enterpriseWorkspace", label: "ダッシュボード", route: "/menupb2b", tags: [] },
        { key: "jobDescriptions", label: "求人", route: "/jdv1", tags: [] },
        { key: "fullenrich", label: "検索", route: "/aqcand", tags: [] },
        { key: "cvwa", label: "CVライブラリ", route: "/cvwa", tags: [] },
        { type: "divider" },
        { key: "selectingCandidate", label: "候補者リスト", route: "/fastscoring", tags: [] },
        { key: "submissions", label: "評価", route: "/submissions", tags: [] },
        { type: "divider" },
        { key: "conversations", label: "Emilyチャット", route: "/conversations", tags: [] },
        { key: "pipeline", label: "Pipeline", route: "/job4manyv2", tags: [] },

        { type: "divider" },
        { type: "section", label: "高度なツール", items: [
          { key: "outplacement", label: "外部検索", route: "/outplacement", tags: [], hideWhenCollapsed: true },
          { key: "inplacement", label: "社内マッチング", route: "/inplacement", tags: [], hideWhenCollapsed: true },
          { key: "historique", label: "履歴", route: "/historique", tags: [], hideWhenCollapsed: true },
          { key: "googlejobs", label: "Google Jobs", route: "/googlejobs", tags: [], hideWhenCollapsed: true },
          { key: "vivier", label: "人材プール", route: "/vivier", tags: [], hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { type: "section", label: "管理者-ボット", adminOnly: true, items: [
          { key: "writerBot", label: "Writer Bot", route: "/writer-bot", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "botCommandCenter", label: "ボットコマンドセンター", route: "/bot-command-center", tags: [], adminOnly: true, hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { key: "settings", label: "設定", route: "/settings", tags: ["企業", "チーム"] }
      ],
      explainByKey: {
        enterpriseWorkspace: "概要",
        jobDescriptions: "職務記述書",
        fullenrich: "人材を探す",
        cvwa: "保存済みCV",
        selectingCandidate: "トップ候補者",
        submissions: "適合結果",
        conversations: "会話",
        pipeline: "候補者ステータス",
        followUp: "面接と申請ステータスの追跡。",
        settings: "企業およびチーム設定の管理。",
        outplacement: "公開求人",
        inplacement: "社内求人",
        historique: "保存済みマッチ",
        googlejobs: "求人検索",
      }
    },
    he: {
      brandTitle: "TrueCalling",
      brandMeta: "סביבת עבודה לארגונים",
      items: [
        { key: "enterpriseWorkspace", label: "לוח בקרה", route: "/menupb2b", tags: [] },
        { key: "jobDescriptions", label: "משרות", route: "/jdv1", tags: [] },
        { key: "fullenrich", label: "חיפוש", route: "/aqcand", tags: [] },
        { key: "cvwa", label: "ספריית קורות חיים", route: "/cvwa", tags: [] },
        { type: "divider" },
        { key: "selectingCandidate", label: "רשימה קצרה", route: "/fastscoring", tags: [] },
        { key: "submissions", label: "הערכות", route: "/submissions", tags: [] },
        { type: "divider" },
        { key: "conversations", label: "צ׳אט Emily", route: "/conversations", tags: [] },
        { key: "pipeline", label: "Pipeline", route: "/job4manyv2", tags: [] },

        { type: "divider" },
        { type: "section", label: "כלים מתקדמים", items: [
          { key: "outplacement", label: "חיפוש חיצוני", route: "/outplacement", tags: [], hideWhenCollapsed: true },
          { key: "inplacement", label: "התאמה פנימית", route: "/inplacement", tags: [], hideWhenCollapsed: true },
          { key: "historique", label: "היסטוריה", route: "/historique", tags: [], hideWhenCollapsed: true },
          { key: "googlejobs", label: "Google Jobs", route: "/googlejobs", tags: [], hideWhenCollapsed: true },
          { key: "vivier", label: "מאגר טאלנטים", route: "/vivier", tags: [], hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { type: "section", label: "ניהול - בוטים", adminOnly: true, items: [
          { key: "writerBot", label: "Writer Bot", route: "/writer-bot", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "botCommandCenter", label: "מרכז פקודות בוט", route: "/bot-command-center", tags: [], adminOnly: true, hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { key: "settings", label: "הגדרות", route: "/settings", tags: ["ארגון", "צוות"] }
      ],
      explainByKey: {
        enterpriseWorkspace: "סקירה כללית",
        jobDescriptions: "תיאורי תפקיד",
        fullenrich: "מציאת טאלנטים",
        cvwa: "קורות חיים שמורים",
        selectingCandidate: "מועמדים מובילים",
        submissions: "תוצאות התאמה",
        conversations: "שיחות",
        pipeline: "סטטוס מועמדים",
        followUp: "עקוב אחר סטטוס ראיון והגשה.",
        settings: "נהל הגדרות ארגון וצוות.",
        outplacement: "משרות ציבוריות",
        inplacement: "משרות פנימיות",
        historique: "התאמות שמורות",
        googlejobs: "חיפוש משרות",
      }
    },
    ar: {
      brandTitle: "TrueCalling",
      brandMeta: "مساحة عمل الشركات",
      items: [
        { key: "enterpriseWorkspace", label: "لوحة التحكم", route: "/menupb2b", tags: [] },
        { key: "jobDescriptions", label: "الوظائف", route: "/jdv1", tags: [] },
        { key: "fullenrich", label: "البحث", route: "/aqcand", tags: [] },
        { key: "cvwa", label: "مكتبة السير الذاتية", route: "/cvwa", tags: [] },
        { type: "divider" },
        { key: "selectingCandidate", label: "القائمة المختصرة", route: "/fastscoring", tags: [] },
        { key: "submissions", label: "التقييمات", route: "/submissions", tags: [] },
        { type: "divider" },
        { key: "conversations", label: "دردشة Emily", route: "/conversations", tags: [] },
        { key: "pipeline", label: "Pipeline", route: "/job4manyv2", tags: [] },

        { type: "divider" },
        { type: "section", label: "أدوات متقدمة", items: [
          { key: "outplacement", label: "بحث خارجي", route: "/outplacement", tags: [], hideWhenCollapsed: true },
          { key: "inplacement", label: "مطابقة داخلية", route: "/inplacement", tags: [], hideWhenCollapsed: true },
          { key: "historique", label: "السجل", route: "/historique", tags: [], hideWhenCollapsed: true },
          { key: "googlejobs", label: "Google Jobs", route: "/googlejobs", tags: [], hideWhenCollapsed: true },
          { key: "vivier", label: "مجموعة المواهب", route: "/vivier", tags: [], hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { type: "section", label: "الإدارة - الروبوتات", adminOnly: true, items: [
          { key: "writerBot", label: "Writer Bot", route: "/writer-bot", tags: [], adminOnly: true, hideWhenCollapsed: true },
          { key: "botCommandCenter", label: "مركز قيادة البوت", route: "/bot-command-center", tags: [], adminOnly: true, hideWhenCollapsed: true },
        ]},
        { type: "divider" },
        { key: "settings", label: "الإعدادات", route: "/settings", tags: ["مؤسسة", "فريق"] }
      ],
      explainByKey: {
        enterpriseWorkspace: "نظرة عامة",
        jobDescriptions: "أوصاف الوظائف",
        fullenrich: "العثور على المواهب",
        cvwa: "سير ذاتية محفوظة",
        selectingCandidate: "أفضل المرشحين",
        submissions: "نتائج الملاءمة",
        conversations: "المحادثات",
        pipeline: "حالة المرشحين",
        followUp: "تتبع حالة المقابلة والتطبيق.",
        settings: "إدارة إعدادات المؤسسة والفريق.",
        outplacement: "وظائف عامة",
        inplacement: "وظائف داخلية",
        historique: "مطابقات محفوظة",
        googlejobs: "بحث عن وظائف",
      }
    }
  };

  const cfg = dict[L] || dict.en;

  // Vivier : item natif de la section « Outils avancés » de chaque langue (i18n).
  const items = cfg.items;

  return { ...cfg, items, badges: {}, activeKey: "enterpriseWorkspace" };
}

// ── t() and createTr() ────────────────────────────────────────────────────────
/** Translate a UI key. Falls back to English, then to the key itself. */
export function t(lang: Language | string, key: string): string {
  const L = ((lang as string) || 'en').toLowerCase() as Language;
  const localeUi = getLocale(L).ui as Record<string, string>;
  const enUi = enLocale.ui as Record<string, string>;
  return localeUi?.[key] ?? enUi[key] ?? key;
}

/** Create a translation object that maps key access to t(lang, key). */
export function createTr(lang: Language | string): Record<string, string> {
  return new Proxy({}, {
    get: (_target, prop) => t(lang, String(prop))
  });
}